import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Grid,
  Tabs,
  Tab,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import EastIcon from "@mui/icons-material/East";
import WestIcon from "@mui/icons-material/West";
import { useStyles } from "../../../theme/style";
import { useDispatch, useSelector } from "react-redux";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { updateAccLibraryJsonData } from "../../../redux/slice/dataSlice";

// Simple Card Component with Hover Effect
const SimpleCard = ({ data, selected = false, disabled = false }) => {
  const theme = useTheme();
  return (
    <Card
      onClick={!disabled ? () => window.open(data.link, "_blank") : undefined}  
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease-in-out",
        cursor: disabled ? "not-allowed" : "pointer",
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
        opacity: disabled ? 0.6 : 1,
        filter: disabled ? "grayscale(50%)" : "none",
        "&:hover": disabled
          ? {}
          : {
              boxShadow: theme.shadows[8],
              transform: "translateY(-4px)",
            },
        position: "relative",
      }}
    >
      {/* Card content - customize this section as needed */}
      <Box sx={{ p: 2, flexGrow: 1 }}>
        {/* You can design your card content here */}
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          {data.title || "Card Title"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Array.isArray(data.description) && data.description.length > 0 ? (
            <ul>
              {data.description.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          ) : (
            data.description || "Card description goes here"
          )}
        </Typography>
      </Box>
    </Card>
  );
};

// Sample data - replace with your actual data
const sampleCards = [
  {
    id: 1,
    title: "React UI Templates",
    description: [
      "Chat",
      "Input",
      "Markdown Renderer",
      "Source-Highlighting",
      "Token Counter",
      "Model Selector",
    ],
    link: "https://dev.azure.com/RB-Group/IT_and_D_Project/_git/Nutri-GenAI-Platform-Acc-UI-Templates",
  },
  {
    id: 2,
    title: "Base Streamlit app with sample reusable layouts",
    description: "",
    link: "https://dev.azure.com/RB-Group/IT_and_D_Project/_git/Nutri-GenAI-Platform-Acc-RAG-Chatbot-Templates",
  },
  {
    id: 3,
    title: "Full Stack App Templates Frontend",
    description: ["Q&A bots", "Summarizers", "Validators", "Extractors"],
    link: "https://dev.azure.com/RB-Group/IT_and_D_Project/_git/Nutri-GenAI-Platform-Acc-Full-Stack-Templates",
  },
  {
    id: 4,
    title: "Full Stack App Templates Backend",
    description: ["Q&A bots", "Summarizers", "Validators", "Extractors"],
    link: "https://dev.azure.com/RB-Group/IT_and_D_Project/_git/Nutri-GenAI-Platform-Acc-App-Templates",
  },
];

const MCPServers = [
  {
    title: "MCP Databricks Server",
    description:
      "This is a Model Context Protocol (MCP) server for executing SQL queries against Databricks using the Statement Execution API",
    // link: "https://glama.ai/mcp/servers/@RafaelCartenet/mcp-databricks-server",
    link: "",
  },
  {
    title: "Azure Cost MCP",
    description:
      "Exposing Apptio API’s to language models for Admins to find the cost of resources",
    // link: "https://glama.ai/mcp/servers/@Rudra-ravi/wikipedia-mcp",
    link: "",
  },
  {
    title: "Postgres MCP server ",
    description: "MCP server to interact with Postgresql Database",
    // link: "https://glama.ai/mcp/servers/@gldc/mcp-postgres",
    link: "",
  },
  {
    title: "Supply Bot MCP",
    description: "MCP Server to converse with SAP Orders",
    link: "",
  },
  {
    title: "Markdown to PDF MCP server",
    description: "MCP tool to convert markdown to PDF file",
    link: "",
  },
  {
    title: "CSV Excel Generator MCP",
    description: "Tool for csv and excel generation from text data",
    link: "",
  },
  {
    title: "Docs MCP",
    description: "Toole for Docs generation from text data",
    link: "",
  },
  {
    title: "ADLS/BLOB MCP Server",
    description: "Tool to access Azure Blob Storage",
    link: "",
  },
];

const AcceleratorLibraryPage = ({
  handleNext,
  handleBack,
  isRegistering,
  register_id,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [isChecked, setIsChecked] = useState(false);
  const [mcpCheckedItems, setMcpCheckedItems] = useState({});

  // Get selected MCP servers from Redux
  const selectedMcpServers = useSelector((state) => 
    state.data.rapidData?.accLibraryJson?.selectedMcpServers || []
  );

  // Sync local state with Redux data on component mount and when Redux data changes
  useEffect(() => {
    if (selectedMcpServers.length > 0) {
      const checkedItems = {};
      selectedMcpServers.forEach(title => {
        checkedItems[title] = true;
      });
      setMcpCheckedItems(checkedItems);
    }
  }, [selectedMcpServers]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleMcpCheckboxChange = (title) => (event) => {
    const isChecked = event.target.checked;
    
    setMcpCheckedItems((prev) => ({
      ...prev,
      [title]: isChecked,
    }));

    // Update Redux with selected MCP titles
    const updatedCheckedItems = {
      ...mcpCheckedItems,
      [title]: isChecked,
    };
    
    // Get list of selected MCP titles
    const selectedMcpTitles = Object.keys(updatedCheckedItems).filter(
      key => updatedCheckedItems[key] === true
    );
    
    // Store in Redux
    dispatch(updateAccLibraryJsonData({
      selectedMcpServers: selectedMcpTitles
    }));
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(register_id)
      .then(() => {
        dispatch(
          updateAlertInfo({
            message: "Copied",
            open: true,
            severity: "success",
          })
        );
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <>
      <Box
        sx={{ maxHeight: isRegistering ? "88%" : "100%", overflowY: "hidden" }}>
        {!isRegistering && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: theme.size[8],
            }}
          >
            <div
              style={{
                marginBottom: theme.size[12],
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.size[14],
                  fontWeight: theme.fontWeight[500],
                }}
              >
                Request ID: {register_id}
              </Typography>
              <ContentCopyIcon
                sx={{
                  marginLeft: theme.size[8],
                  fontSize: theme.size[18],
                  color: theme.palette.secondary.main,
                  cursor: "pointer",
                }}
                onClick={copyToClipboard}
              />
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="accelerator tabs"
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Tab
              label="Accelerator Libraries"
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab label="Agentic" id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box
            role="tabpanel"
            id="tabpanel-0"
            aria-labelledby="tab-0"
            sx={{
              overflowY: "auto",
              maxHeight: "60vh",
              mb: 2,
              pr: 1,
            }}
          >
            {/* Request Repos */}
            <Box
              sx={{
                mb: 4,
                p: 3,
                textAlign: "center",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[1],
                opacity: !isRegistering ? 0.6 : 1,
                filter: !isRegistering ? "grayscale(50%)" : "none",
                pointerEvents: !isRegistering ? "none" : "auto",
              }}
            >
              <Typography
                variant="h6"
                component="a"
                sx={{
                  color: !isRegistering
                    ? theme.palette.grey[500]
                    : theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                }}
              >
                Please reach out to the Ops team for getting access to the relevant Code repo
              </Typography>
            </Box>

            {/* Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                {sampleCards.map((card) => (
                  <Grid item key={card.id} xs={12} sm={6} md={6}>
                    <SimpleCard data={card} disabled={!isRegistering} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}

        {/* Agentics Tab Content */}
        {activeTab === 1 && (
          <Box
            role="tabpanel"
            id="tabpanel-1"
            aria-labelledby="tab-1"
            sx={{
              overflowY: "auto",
              maxHeight: "60vh",
              mb: 2,
              pr: 1,
            }}
          >
            {/* Section 1 */}
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.paper,
                  mb: 1.5,
                  transition: "all 0.3s ease",
                  opacity: !isRegistering ? 0.6 : 1,
                  filter: !isRegistering ? "grayscale(50%)" : "none",
                  pointerEvents: !isRegistering ? "none" : "auto",
                  "&:hover": !isRegistering
                    ? {}
                    : {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.primary.light,
                      },
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    color: theme.palette.primary.main,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🤖 MJN GPT
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.4,
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  MJNGPT supports multiple AI providers with features like AI
                  model switching, message search, create no code agents with
                  files as knowledge base
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.paper,
                  transition: "all 0.3s ease",
                  opacity: !isRegistering ? 0.6 : 1,
                  filter: !isRegistering ? "grayscale(50%)" : "none",
                  pointerEvents: !isRegistering ? "none" : "auto",
                  "&:hover": !isRegistering
                    ? {}
                    : {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.secondary.light,
                      },
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    color: theme.palette.secondary.main,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ⚡ MCP Servers for Functionalities
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.4,
                    color: theme.palette.text.secondary,
                    fontStyle: "italic",
                    fontSize: "0.875rem",
                  }}
                >
                  <ul>
                    <li>
                      You can also select from the library of re-usable MCP
                      servers to fast-track your development.
                    </li>
                    <li>
                      On selecting the below MCP servers, access/api key will be
                      provided to utilize the servers within your application.
                    </li>
                  </ul>
                </Typography>
              </Box>
            </Box>

            {/* Horizontal Divider 1 */}
            <Box
              sx={{
                height: 2,
                backgroundColor: theme.palette.grey[400],
                my: 2,
                borderRadius: 1,
              }}
            />

            {/* Section 2 */}
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.paper,
                  transition: "all 0.3s ease",
                  opacity: !isRegistering ? 0.6 : 1,
                  filter: !isRegistering ? "grayscale(50%)" : "none",
                  pointerEvents: !isRegistering ? "none" : "auto",
                  "&:hover": !isRegistering
                    ? {}
                    : {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.warning.light,
                        backgroundColor: theme.palette.action.hover,
                      },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    disabled={!isRegistering}
                    sx={{
                      color: theme.palette.primary.main,
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                      "&.Mui-disabled": {
                        opacity: 0.6,
                      },
                      p: 0.5,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: theme.palette.warning.dark,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🚀 Create your own Custom Agentic System
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Horizontal Divider 2 */}
            <Box
              sx={{
                height: 2,
                backgroundColor: theme.palette.grey[400],
                my: 2,
                borderRadius: 1,
              }}
            />

            {/* Section 3 */}
            <Box sx={{ py: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  mb: 1.5,
                  color: theme.palette.primary.main,
                  pb: 0.5,
                }}
              >
                🔧 MCP Servers
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {MCPServers.map((item) => {
                  return (
                    <Box
                      key={item.title}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        transition: "all 0.3s ease",
                        opacity: !isRegistering ? 0.6 : 1,
                        filter: !isRegistering ? "grayscale(50%)" : "none",
                        pointerEvents: !isRegistering ? "none" : "auto",
                        "&:hover": !isRegistering
                          ? {}
                          : {
                              boxShadow: theme.shadows[1],
                              borderColor: theme.palette.primary.light,
                              backgroundColor: theme.palette.action.hover,
                            },
                      }}
                    >
                      <Checkbox
                        checked={mcpCheckedItems[item.title] || false}
                        onChange={handleMcpCheckboxChange(item.title)}
                        disabled={!isRegistering}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                          "&.Mui-disabled": {
                            opacity: 0.6,
                          },
                          p: 0.5,
                        }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            cursor: item.link ? "pointer" : "default",
                            color: theme.palette.text.primary,
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            mb: item.description ? 0.5 : 0,
                            "&:hover": item.link
                              ? {
                                  textDecoration: "underline",
                                }
                              : {},
                          }}
                          onClick={() =>
                            item.link ? window.open(item.link, "_blank") : null
                          }
                        >
                          {item.title}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.75rem",
                              lineHeight: 1.3,
                              display: "block",
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {isRegistering && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            position: "absolute",
            width: "100%",
            bottom: 0,
          }}
        >
          <div style={{ display: "flex" }}>
            <div>
              <Button
                disableRipple
                className={classes.outlinedButton}
                onClick={() => handleBack()}
                style={{
                  fontWeight: theme.fontWeight[500],
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <WestIcon
                    sx={{
                      fontSize: theme.size[18],
                      marginRight: theme.size[4],
                    }}
                  />
                  <span>Back</span>
                </div>
              </Button>
              <div
                style={{
                  fontSize: theme.size[12],
                  fontWeight: theme.fontWeight[500],
                  color: theme.palette.rapidInfra.text,
                  paddingTop: theme.size[4],
                }}
              >
                Back to Project Type and Components
              </div>
            </div>
          </div>
          <div>
            <Button
              disableRipple
              className={classes.filledButton}
              onClick={() => handleNext()}
              style={{
                fontWeight: theme.fontWeight[500],
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Next</span>
                <EastIcon
                  sx={{
                    fontSize: theme.size[18],
                    marginLeft: theme.size[4],
                  }}
                />
              </div>
            </Button>
            <div
              style={{
                fontSize: theme.size[12],
                fontWeight: theme.fontWeight[500],
                color: theme.palette.rapidInfra.text,
                paddingTop: theme.size[4],
                textAlign: "right",
              }}
            >
              Choose AI Model
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AcceleratorLibraryPage;
